"use client";

import React from "react";
import { useTranslation } from "react-i18next";

interface ExampleCalculationProps {
  petType: "cat" | "dog";
  petName: string;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
}

const ExampleCalculation: React.FC<ExampleCalculationProps> = ({
  petType,
  petName,
  yearlyDeductible,
  coPayCoveragePercentage,
}) => {
  const { t } = useTranslation();

  const isHighDeductible = yearlyDeductible > 300;
  const exampleVetCost = isHighDeductible ? 1470 : 490;

  const coPayValue = (100 - coPayCoveragePercentage) / 100;
  const deductibleApplied = Math.min(yearlyDeductible, exampleVetCost);
  const remainingAfterDeductible = Math.max(0, exampleVetCost - deductibleApplied);
  const yourCoPayAmount = Math.round(remainingAfterDeductible * coPayValue);
  const insurancePays = Math.round(remainingAfterDeductible - yourCoPayAmount);

  const descriptionKey = isHighDeductible
    ? "priceCustomization.exampleCalculation.dentalDescription"
    : "priceCustomization.exampleCalculation.description";

  const scenarioKey = isHighDeductible
    ? "priceCustomization.exampleCalculation.dentalScenario"
    : "priceCustomization.exampleCalculation.scenario";

  return (
    <div className="space-y-6"> {/* Removed border-t pt-6 */}
      <div className="p-6 space-y-4 bg-secondary/10 dark:bg-secondary/20 rounded-lg">
        <p className="text-sm text-primary font-semibold mb-2">{t("claims-calculation-how-it-works")}</p>
        <p className="text-xl font-bold text-foreground">
          {t(descriptionKey, { vetCost: exampleVetCost.toLocaleString('de-CH') })}
        </p>
        <p className="text-base text-foreground">
          {t(scenarioKey, { petName: petName, vetCost: exampleVetCost.toLocaleString('de-CH') })}
        </p>

        <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-4 text-lg">
          <span className="text-foreground">{t("claims-calculation-total")}</span>
          <span className="font-semibold text-right text-foreground">CHF {exampleVetCost.toLocaleString('de-CH')}</span>

          <span className="text-foreground">{t("priceCustomization.exampleCalculation.deductible", { yearlyDeductible: yearlyDeductible })}</span>
          <span className="font-semibold text-right text-foreground">- CHF {deductibleApplied.toLocaleString('de-CH')}</span>

          <span className="text-foreground">{t("priceCustomization.exampleCalculation.coPay", { coPayPercentage: coPayValue * 100 })}</span>
          <span className="font-semibold text-right text-foreground">- CHF {yourCoPayAmount.toLocaleString('de-CH')}</span>

          <div className="col-span-2 border-t-2 border-dashed border-foreground/50 pt-4 mt-2"></div>

          <span className="font-bold text-foreground text-xl">{t("priceCustomization.exampleCalculation.insurancePays")}</span>
          <span className="font-bold text-foreground text-xl text-right">CHF {insurancePays.toLocaleString('de-CH')}</span>
        </div>
      </div>
    </div>
  );
};

export default ExampleCalculation;
